import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "../../Components/Grid/Grid";
import Modal from "../../Components/Modal/Change/ChangeModel";
import Button from "../../Components/button/button";
import "../../Style/IncidentView.css";
import config from "../../config";
import { ChangeData } from "../../utils/Change/changeTypes";
import getColumnDefs from "../../utils/Change/changecolumnDefs";
import { formatDuration } from "../../utils/durationUtils";
import { authFetch } from "../../utils/Security/authFetch";
import { GridApi } from "ag-grid-community";
import localforage from "localforage";

interface PersistedState {
  searchQuery: string;
  selectedChangeId: string | null;
  isModalOpen: boolean;
  gridFilterModel: any;
  gridSortModel: any;
}

function ChangeView() {
  const username = localStorage.getItem("username") || "System";
  const userTeam = localStorage.getItem("position");
  const userId = localStorage.getItem("userid");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const PERSISTENCE_KEY = `changeView_state_${isAdmin ? "admin" : userId}`;
  const INCIDENT_CACHE_KEY = `incidents_${isAdmin ? "admin" : userId}`;
  const [loading, setLoading] = useState(true);
  const CACHE_KEY = `changes_${isAdmin ? "admin" : userId}`;
  const isForTelebras = localStorage.getItem("isForTelebras") === "true";
const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";
  const isForNilesat = localStorage.getItem("isForNilesat") === "true";

  const [rowData, setRowData] = useState<ChangeData[]>([]);
  const [filteredData, setFilteredData] = useState<ChangeData[]>([]);
  const [selectedChange, setSelectedChange] = useState<ChangeData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const navigate = useNavigate();
  const gridApiRef = useRef<GridApi | null>(null);

  // ------------------- Persistence Helpers -------------------
  const saveGridStateToStorage = () => {
    if (!gridApiRef.current) return;

    const filterModel = gridApiRef.current.getFilterModel();
    const sortModel = (gridApiRef.current.getColumnState() ?? [])
      .filter((col) => col.sort != null)
      .map((col) => ({
        colId: col.colId,
        sort: col.sort,
        sortIndex: col.sortIndex,
      }));

    const stateToSave: PersistedState = {
      searchQuery,
      selectedChangeId: selectedChange?.ticketId || null,
      isModalOpen,
      gridFilterModel: filterModel,
      gridSortModel: sortModel,
    };

    localStorage.setItem(PERSISTENCE_KEY, JSON.stringify(stateToSave));
  };

  const loadPersistedState = (): PersistedState | null => {
    try {
      const savedState = localStorage.getItem(PERSISTENCE_KEY);
      if (savedState) return JSON.parse(savedState) as PersistedState;
    } catch (error) {
      console.error("Error loading persisted state:", error);
    }
    return null;
  };

  const loadMyTodo = async () => {
    if (!username) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/changes/my-todo/${username}`
      );
      const data: ChangeData[] = await res.json();

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load My Todo", e);
    } finally {
      setLoading(false);
    }
  };
  // ✅ RESET TO FULL LIST (from cache or API)
  const handleReset = async () => {
    setLoading(true);
    setSearchQuery("");

    try {
      // 1️⃣ Try restoring from cache first
      const cached = await localforage.getItem<{
        rows: ChangeData[];
        ts: number;
      }>(CACHE_KEY);

      if (cached?.rows?.length) {
        setRowData(cached.rows);
        setFilteredData(cached.rows);
      } else {
        // 2️⃣ If cache is empty, force reload from API
        window.location.reload();
      }
    } catch (e) {
      console.error("Reset failed, reloading page", e);
      window.location.reload();
    } finally {
      setLoading(false);
    }
  };

  // ✅ MY GROUP TODO
  const loadMyGroupTodo = async () => {
    if (!userTeam) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/changes/my-group-todo/${userTeam}`
      );
      const data: ChangeData[] = await res.json();

      setRowData(data);
      setFilteredData(data);
    } catch (e) {
      console.error("Failed to load Group Todo", e);
    } finally {
      setLoading(false);
    }
  };

  // ------------------- Grid Handlers -------------------
  // ------------------- Grid Handlers -------------------
  const handleGridReady = (params: { api: GridApi }) => {
    gridApiRef.current = params.api;

    // Restore persisted grid filter/sort
    const savedState = localStorage.getItem(PERSISTENCE_KEY);
    if (savedState) {
      try {
        const parsed: PersistedState = JSON.parse(savedState);
        if (parsed.gridFilterModel)
          params.api.setFilterModel(parsed.gridFilterModel);
        if (parsed.gridSortModel?.length) {
          const columnState = params.api.getColumnState() ?? [];
          parsed.gridSortModel.forEach((sort: any) => {
            const col = columnState.find((c) => c.colId === sort.colId);
            if (col) {
              col.sort = sort.sort;
              col.sortIndex = sort.sortIndex;
            }
          });
          params.api.applyColumnState({ state: columnState });
        }
      } catch (error) {
        console.error("Error restoring grid state:", error);
      }
    }

    // ----------------- NEW: Ensure Set Filter dropdowns restore correctly -----------------
    params.api.addEventListener("firstDataRendered", () => {
      if (savedState) {
        try {
          const parsed: PersistedState = JSON.parse(savedState);
          if (parsed.gridFilterModel) {
            // Apply again after data is rendered to make sure set filters work
            params.api.setFilterModel(parsed.gridFilterModel);
          }
        } catch (error) {
          console.error(
            "Error restoring filter model on firstDataRendered:",
            error
          );
        }
      }
    });

    // Persist changes immediately
    const saveListener = () => saveGridStateToStorage();
    gridApiRef.current.addEventListener("filterChanged", saveListener);
    gridApiRef.current.addEventListener("sortChanged", saveListener);

    // Cleanup listeners
    return () => {
      gridApiRef.current?.removeEventListener("filterChanged", saveListener);
      gridApiRef.current?.removeEventListener("sortChanged", saveListener);
    };
  };

  // ------------------- Fetch Changes -------------------
  useEffect(() => {
    localforage.removeItem(INCIDENT_CACHE_KEY);

    const persisted = loadPersistedState();

    const showRows = (rows: ChangeData[]) => {
      setRowData(rows);
      setFilteredData(
        rows.filter((item: ChangeData) =>
          applySearchFilter(item, persisted?.searchQuery || "")
        )
      );
    };

    const fetchChanges = async () => {
      if (isAdmin && (isForTelebras || isForPetrobras || isForNilesat)) {
        setRowData([]);
        setFilteredData([]);
        setLoading(false);
        return;
      }

      if (persisted) {
        setSearchQuery(persisted.searchQuery);
        if (persisted.isModalOpen && persisted.selectedChangeId) {
          openChange(persisted.selectedChangeId);
        }
      }

      // Show the last loaded list straight away while fresh data loads
      try {
        const cached = await localforage.getItem<{
          rows: ChangeData[];
          ts: number;
        }>(CACHE_KEY);
        if (cached?.rows?.length) {
          showRows(cached.rows);
          setIsInitialized(true);
          setLoading(false);
        }
      } catch (e) {
        console.error("Failed to read cached changes", e);
      }

      try {
        const baseUrl = isAdmin
          ? `${config.apiBaseUrl}/api/changes`
          : `${config.apiBaseUrl}/api/changes/user/${userId}`;

        // Lightweight list (no journal / communication / attachments);
        // fall back to the full list if the backend doesn't have it yet
        // (authFetch throws on non-2xx responses)
        let response: Response;
        try {
          response = await authFetch(`${baseUrl}/list`);
        } catch (e) {
          console.warn("Light change list unavailable, loading full list", e);
          response = await authFetch(baseUrl);
        }

        const data = await response.json();
        const formatted: ChangeData[] = data.map((item: any) => ({
          ...item,
          changeResolutionTime: formatDuration(item.changeResolutionTime),
        }));

        showRows(formatted);
        setIsInitialized(true);
        localforage
          .setItem(CACHE_KEY, { rows: formatted, ts: Date.now() })
          .catch((e) => console.error("Failed to cache changes", e));
      } catch (error) {
        console.error("Error fetching changes:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchChanges();
  }, [isAdmin, userId]);

  // ------------------- Filter Handling -------------------
  const applySearchFilter = (item: ChangeData, query: string = searchQuery) => {
    const lowerQuery = query.toLowerCase();
    return Object.values(item).some(
      (value) =>
        value != null && value.toString().toLowerCase().includes(lowerQuery)
    );
  };

  useEffect(() => {
    if (isInitialized) {
      setFilteredData(
        searchQuery
          ? rowData.filter((item) => applySearchFilter(item, searchQuery))
          : rowData
      );
      saveGridStateToStorage();
    }
  }, [searchQuery, rowData, selectedChange, isModalOpen, isInitialized]);

  // ------------------- Task Handlers -------------------
  // The grid rows are lightweight, so load the full change for the modal
  const openChange = async (ticketId: string, row?: ChangeData) => {
    if (row) setSelectedChange(row);
    setIsModalOpen(true);
    try {
      const res = await authFetch(`${config.apiBaseUrl}/api/changes/${ticketId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const full = await res.json();
      setSelectedChange((prev) =>
        // Skip if the user closed or switched rows while this was loading
        prev?.ticketId === ticketId || (!row && !prev)
          ? {
              ...full,
              changeResolutionTime: formatDuration(full.changeResolutionTime),
            }
          : prev
      );
    } catch (e) {
      console.error("Failed to load change details", e);
    }
  };

  const handleRowClick = (data: ChangeData) => {
    openChange(data.ticketId, data);
  };

  const handleEdit = (data: ChangeData) => {
    navigate(`/change-edit/${data.ticketId}`, { state: { change: data } });
  };

  const handleDelete = async (ticketId: string) => {
    if (!window.confirm("Are you sure you want to delete this change?")) return;

    try {
      const response = await authFetch(
        `${config.apiBaseUrl}/api/changes/${ticketId}`,
        { method: "DELETE" }
      );
      if (response.ok) {
        const updatedData = rowData.filter((c) => c.ticketId !== ticketId);
        setRowData(updatedData);
        setFilteredData(updatedData.filter((item) => applySearchFilter(item)));
        if (selectedChange?.ticketId === ticketId) closeModal();
      } else {
        console.error("Failed to delete change");
      }
    } catch (error) {
      console.error("Error deleting change:", error);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedChange(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // ------------------- CSV Export -------------------
  const handleExportCSV = () => {
    if (!gridApiRef.current) return;

    const filteredNodes: ChangeData[] = [];
    gridApiRef.current.forEachNodeAfterFilter((node) => {
      if (node.data) filteredNodes.push(node.data);
    });
    if (!filteredNodes.length) return;

    const exportableColumns = getColumnDefs(handleEdit, handleDelete).filter(
      (col) => !!col.field
    );
    const headers = exportableColumns.map((col) => col.headerName || col.field);
    const fields = exportableColumns.map(
      (col) => col.field as keyof ChangeData
    );

    const sanitizeField = (value: any, field: string) => {
      if (value == null) return "";
      if (
        [
          "summary",
          "description",
          "solution",
          "risk",
          "rollbackstrategy",
        ].includes(field)
      ) {
        value = String(value).replace(/<[^>]*>/g, "");
      }
      if (
        typeof value === "string" &&
        /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)
      ) {
        value = value.replace("T", " ").split(".")[0];
      }
      return `"${String(value)
        .replace(/"/g, '""')
        .replace(/\r?\n|\r/g, " ")}"`;
    };

    const csvRows = [
      headers.join(","),
      ...filteredNodes.map((row) =>
        fields.map((f) => sanitizeField(row[f], f)).join(",")
      ),
    ];

    const blob = new Blob(["﻿" + csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Change.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // ------------------- Render -------------------
  return (
    <>
      <div className="top">
        <div className="add">
          <div className="right-section">
{ !isForTelebras && !isForPetrobras && !isForNilesat &&(
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/change-add")}
            >
              Add
            </Button>
)}
            <Button variant="primary" size="md" onClick={handleExportCSV}>
              Export to CSV
            </Button>
            <Button variant="primary" size="md" onClick={loadMyTodo}>
              My To-Do List
            </Button>

            <Button variant="primary" size="md" onClick={loadMyGroupTodo}>
              My Group To-Do List
            </Button>
            <Button variant="primary" size="md" onClick={handleReset}>
              Reset
            </Button>
            <input
              type="text"
              placeholder="Full Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                padding: "8px",
                borderRadius: "5px",
                border: "1px solid gray",
                width: "400px",
              }}
            />
          </div>
        </div>
      </div>

      <div className="Grid" style={{ height: "82%" }}>
        <Grid<ChangeData>
          rowData={filteredData}
          columnDefs={getColumnDefs(handleEdit, handleDelete)}
          onRowClick={handleRowClick}
          onGridReady={handleGridReady}
        />
      </div>

      {/* Wait for the change to load: the modal crashes on null data */}
      <Modal
        isOpen={isModalOpen && selectedChange !== null}
        data={selectedChange}
        onClose={closeModal}
      />
    </>
  );
}

export default ChangeView;
